import type {
  OperationVariables,
  TypedDocumentNode,
  MaybeMasked,
} from '@apollo/client';
import { useMutation as useBaseMutation } from '@apollo/client/react';
import type { SnackbarKey } from 'notistack';

import type { JSX } from 'react';

import { useSnackbar } from './useSnackbar';

export interface Options<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> extends useBaseMutation.Options<TData, TVariables> {
  completionMessage?: React.ReactNode;
  errorMessage?: React.ReactNode;
  hideErrorMessage?: boolean;
  action?: (key: SnackbarKey) => React.ReactNode | JSX.Element;
}

export function useMutation<TData, TVariables extends OperationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options: Options<TData, TVariables> = {},
) {
  const { enqueueSnackbar } = useSnackbar();

  const oldOnCompleted = options.onCompleted;

  options.onCompleted = (data: MaybeMasked<TData>) => {
    if (options.completionMessage) {
      enqueueSnackbar(options.completionMessage, { action: options.action });
    }

    if (oldOnCompleted) {
      oldOnCompleted(data);
    }
  };

  const oldOnError = options.onError;

  options.onError = (error) => {
    if (options.hideErrorMessage) return;
    enqueueSnackbar(
      options.errorMessage || error.message || 'An unknown error occured',
      { variant: 'error' },
    );

    if (oldOnError) {
      oldOnError(error);
    }
  };

  return useBaseMutation<TData, TVariables>(mutation, options);
}
